class DailyFlashSale extends HTMLElement {
  #deadline = 0;
  #timer;
  #section;
  #timeZone = '';

  connectedCallback() {
    const secondsToReset = Number.parseInt(this.dataset.secondsToReset || '86400', 10);
    this.#timeZone = this.dataset.timeZone || '';
    this.#deadline = this.#timeZone
      ? this.#getNextMidnight(this.#timeZone)
      : Date.now() + Math.max(1, secondsToReset) * 1000;
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener('variant:update', this.#handleVariantUpdate);
    this.addEventListener('click', this.#handleCouponClick);

    this.#renderCountdown();
    this.#renderTimeZoneLabel();
    this.#timer = window.setInterval(() => this.#renderCountdown(), 1000);
    this.#applyQuantityLimit();
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
    this.#section?.removeEventListener('variant:update', this.#handleVariantUpdate);
    this.removeEventListener('click', this.#handleCouponClick);
  }

  #renderCountdown() {
    const day = 24 * 60 * 60 * 1000;
    const now = Date.now();

    if (this.#deadline <= now && this.#timeZone) {
      this.#deadline = this.#getNextMidnight(this.#timeZone);
      this.#renderTimeZoneLabel();
    } else {
      while (this.#deadline <= now) {
        this.#deadline += day;
      }
    }

    const totalSeconds = Math.max(0, Math.floor((this.#deadline - now) / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#setTimePart('hours', hours);
    this.#setTimePart('minutes', minutes);
    this.#setTimePart('seconds', seconds);
  }

  #getNextMidnight(timeZone) {
    try {
      const now = new Date();
      const current = this.#getZonedParts(now, timeZone);
      const nextDate = new Date(Date.UTC(current.year, current.month - 1, current.day + 1));
      const target = {
        year: nextDate.getUTCFullYear(),
        month: nextDate.getUTCMonth() + 1,
        day: nextDate.getUTCDate(),
      };
      const targetAsUtc = Date.UTC(target.year, target.month - 1, target.day, 0, 0, 0);
      let estimate = targetAsUtc;

      for (let attempt = 0; attempt < 4; attempt += 1) {
        const offset = this.#getTimeZoneOffset(new Date(estimate), timeZone);
        const corrected = targetAsUtc - offset;
        if (Math.abs(corrected - estimate) < 1000) break;
        estimate = corrected;
      }

      return estimate;
    } catch (error) {
      return Date.now() + 24 * 60 * 60 * 1000;
    }
  }

  #getZonedParts(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(date);
    const values = {};

    parts.forEach((part) => {
      if (part.type !== 'literal') values[part.type] = Number.parseInt(part.value, 10);
    });

    return values;
  }

  #getTimeZoneOffset(date, timeZone) {
    const parts = this.#getZonedParts(date, timeZone);
    const zonedAsUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second
    );
    const instant = Math.floor(date.getTime() / 1000) * 1000;
    return zonedAsUtc - instant;
  }

  #renderTimeZoneLabel() {
    const label = this.querySelector('[data-time-zone-label]');
    if (!label || !this.#timeZone) return;

    try {
      const part = new Intl.DateTimeFormat('en-US', {
        timeZone: this.#timeZone,
        timeZoneName: 'short',
      })
        .formatToParts(new Date())
        .find((item) => item.type === 'timeZoneName');
      label.textContent = part?.value || 'PT';
    } catch (error) {
      label.textContent = 'PT';
    }
  }

  #setTimePart(part, value) {
    const output = this.querySelector(`[data-time-part="${part}"]`);
    if (output) output.textContent = String(value).padStart(2, '0');
  }

  #handleVariantUpdate = (event) => {
    if (event.detail?.data?.productId !== this.dataset.productId) return;

    const blockId = this.dataset.blockId;
    const candidates = event.detail.data.html?.querySelectorAll('daily-flash-sale') || [];
    const updated = Array.from(candidates).find((candidate) => candidate.dataset.blockId === blockId);
    if (!updated) return;

    this.dataset.remaining = updated.dataset.remaining;
    this.dataset.dailyLimit = updated.dataset.dailyLimit;

    const remaining = this.querySelector('[data-remaining-count]');
    if (remaining) remaining.textContent = updated.dataset.remaining || '0';

    const stockCount = this.querySelector('[data-stock-count]');
    if (stockCount) {
      stockCount.textContent = `${updated.dataset.remaining || '0'} / ${updated.dataset.dailyLimit || '0'}`;
    }

    this.#updateProgress();
    window.setTimeout(() => this.#applyQuantityLimit(), 0);
  };

  #updateProgress() {
    const remaining = Math.max(0, Number.parseInt(this.dataset.remaining || '0', 10));
    const limit = Math.max(1, Number.parseInt(this.dataset.dailyLimit || '1', 10));
    const progress = this.querySelector('[data-stock-progress]');
    const progressBar = this.querySelector('[data-stock-progress-bar]');
    const percentage = Math.min(100, (remaining / limit) * 100);

    if (progress) {
      progress.setAttribute('aria-valuenow', String(remaining));
      progress.setAttribute('aria-valuemax', String(limit));
    }

    if (progressBar instanceof HTMLElement) {
      progressBar.style.width = `${percentage}%`;
    }
  }

  #handleCouponClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-coupon-copy]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    const code = button.dataset.couponCode || '';
    const label = button.querySelector('[data-coupon-copy-label]');
    if (!code || !label) return;

    const defaultText = button.dataset.copyText || 'Copy';
    const copiedText = button.dataset.copiedText || 'Copied!';

    try {
      await this.#copyText(code);
      label.textContent = copiedText;
      button.classList.add('is-copied');
    } catch (error) {
      label.textContent = code;
    }

    window.clearTimeout(button._couponTimer);
    button._couponTimer = window.setTimeout(() => {
      label.textContent = defaultText;
      button.classList.remove('is-copied');
    }, 1800);
  };

  async #copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.inset = '-9999px auto auto -9999px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      if (!document.execCommand('copy')) throw new Error('Copy failed');
    } finally {
      textarea.remove();
    }
  }

  #applyQuantityLimit() {
    if (this.dataset.limitQuantity !== 'true') return;

    const remaining = Math.max(0, Number.parseInt(this.dataset.remaining || '0', 10));
    if (remaining < 1) return;

    const details = this.closest('.product-details');
    const selector = details?.querySelector('quantity-selector-component');
    const input = selector?.querySelector('[ref="quantityInput"]');
    if (!(input instanceof HTMLInputElement)) return;

    const currentMax = Number.parseInt(input.max, 10);
    const effectiveMax = Number.isFinite(currentMax) ? Math.min(currentMax, remaining) : remaining;
    const min = input.min || '1';
    const step = input.step || '1';

    if (typeof selector.updateConstraints === 'function') {
      selector.updateConstraints(min, String(effectiveMax), step);
    } else {
      input.max = String(effectiveMax);
    }
  }
}

if (!customElements.get('daily-flash-sale')) {
  customElements.define('daily-flash-sale', DailyFlashSale);
}
